import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ModuleHeader } from '@/components/ui/module-header';
import { DataTable } from '@/components/ui/data-table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { StatusPill } from '@/components/ui/status-pill';
import { Checkbox } from '@/components/ui/checkbox';
import { formatCurrency } from '@/lib/utils';
import * as Icons from 'lucide-react';
import { toast } from 'sonner';
import { api, getApiBase, getToken } from '@/lib/api';

function openExport(path) {
  const token = getToken();
  const url = `${getApiBase()}${path}`;
  if (!token) {
    window.open(url, '_blank');
    return;
  }
  void fetch(url, { headers: { Authorization: `Bearer ${token}` }, credentials: 'include' })
    .then(async (res) => {
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      window.open(href, '_blank');
    })
    .catch(() => toast.error('Export failed'));
}

export default function PayRunsPage() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [label, setLabel] = useState('');
  const [selected, setSelected] = useState([]);

  const dataQ = useQuery({
    queryKey: ['pay-runs-page'],
    queryFn: async () => {
      const [s, p] = await Promise.all([
        api('/api/v1/settlements'),
        api('/api/v1/pay-runs'),
      ]);
      if (s.status !== 200) throw new Error(s.data?.error ?? 'Failed to load settlements');
      if (p.status !== 200) throw new Error(p.data?.error ?? 'Failed to load pay runs');
      return {
        settlements: (s.data.settlements ?? []).filter(
          (x) => !x.payRunId && (!x.needsReview || x.reviewedAt)
        ),
        blockedReview: (s.data.settlements ?? []).filter(
          (x) => !x.payRunId && x.needsReview && !x.reviewedAt
        ),
        payRuns: p.data.payRuns ?? [],
      };
    },
  });

  useEffect(() => {
    if (dataQ.isError) toast.error(dataQ.error?.message ?? 'Failed to load pay runs');
  }, [dataQ.isError, dataQ.error]);

  const settlements = dataQ.data?.settlements ?? [];
  const blockedReview = dataQ.data?.blockedReview ?? [];
  const runs = dataQ.data?.payRuns ?? [];

  const toggleSettlement = (id) => {
    setSelected(selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id]);
  };

  const total = useMemo(
    () =>
      settlements
        .filter((s) => selected.includes(s.id))
        .reduce((sum, s) => sum + (Number(s.netAmount) || 0), 0),
    [settlements, selected]
  );

  const handleCreate = async () => {
    if (selected.length === 0) {
      toast.error('Select settlements');
      return;
    }
    const r = await api('/api/v1/pay-runs', {
      method: 'POST',
      body: { settlementIds: selected, label: label || undefined },
    });
    if (r.status === 201) {
      toast.success('Pay run created');
      setLabel('');
      setSelected([]);
      setShowCreate(false);
      qc.invalidateQueries({ queryKey: ['pay-runs-page'] });
    } else {
      toast.error(String(r.data?.error ?? 'Failed'));
    }
  };

  const postRun = async (id) => {
    const r = await api(`/api/v1/pay-runs/${id}/post`, { method: 'POST', body: {} });
    if (r.status === 200) {
      toast.success('Pay run posted');
      qc.invalidateQueries({ queryKey: ['pay-runs-page'] });
    } else {
      toast.error(String(r.data?.error ?? 'Failed'));
    }
  };

  const columns = [
    { key: 'label', label: 'Label' },
    {
      key: 'period',
      label: 'Period',
      render: (_, row) => `${row.periodStart ?? '—'} → ${row.periodEnd ?? '—'}`,
    },
    {
      key: 'lines',
      label: 'Settlements',
      className: 'font-mono',
      render: (_, row) => row.lines?.length ?? row.settlementIds?.length ?? '—',
    },
    {
      key: 'totalNet',
      label: 'Total',
      render: (v) => <span className="font-mono font-bold">{formatCurrency(Number(v) || 0)}</span>,
    },
    { key: 'status', label: 'Status', render: (v) => <StatusPill status={v} /> },
    {
      key: 'actions',
      label: '',
      render: (_, row) => (
        <div className="flex gap-1">
          {row.status === 'draft' && (
            <Button size="sm" variant="outline" onClick={() => postRun(row.id)}>
              Post
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            title="Download ACH CSV"
            onClick={() => openExport(`/api/v1/pay-runs/${row.id}/export.csv`)}
          >
            <Icons.Download className="h-3 w-3" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            title="Print Stubs"
            onClick={() => openExport(`/api/v1/pay-runs/${row.id}/stubs`)}
          >
            <Icons.Printer className="h-3 w-3" />
          </Button>
        </div>
      ),
    },
  ];

  return (
<div className="space-y-6 motion-enter">
        <ModuleHeader
          title="Pay Runs"
          description="Bundle settlements for ACH payments"
          icon="Banknote"
          metrics={[
            { label: 'Runs', value: runs.length },
            { label: 'Ready', value: settlements.length },
          ]}
          actions={
            <Button onClick={() => setShowCreate(!showCreate)}>
              <Icons.Plus className="h-4 w-4 mr-2" />
              Create Draft Run
            </Button>
          }
        />

        {showCreate && (
          <Card>
            <CardHeader>
              <CardTitle>New Pay Run</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Run Label
                </label>
                <input
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="e.g., Week 4 Payroll"
                  className="mt-2 w-full px-3 py-2 border rounded-md text-sm bg-background outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              <div>
                <h4 className="font-semibold text-sm mb-2">Select Settlements to Include</h4>
                <div className="border rounded-lg divide-y max-h-72 overflow-auto">
                  {settlements.length === 0 ? (
                    <p className="p-3 text-sm text-muted-foreground">
                      {blockedReview.length > 0
                        ? `${blockedReview.length} settlement(s) still need review cleared on Settlements before they appear here.`
                        : 'No eligible settlements (create a settlement and clear review if required).'}
                    </p>
                  ) : (
                    settlements.map((s) => (
                      <label
                        key={s.id}
                        className="flex items-center gap-3 p-3 hover:bg-muted/30 cursor-pointer"
                      >
                        <Checkbox
                          checked={selected.includes(s.id)}
                          onCheckedChange={() => toggleSettlement(s.id)}
                        />
                        <div className="flex-1 flex justify-between">
                          <div>
                            <p className="font-mono text-sm">{s.id.slice(0, 8)}</p>
                            <p className="text-xs text-muted-foreground">
                              {s.driverName} · {s.periodStart}
                            </p>
                          </div>
                          <p className="font-mono font-medium">
                            {formatCurrency(Number(s.netAmount) || 0)}
                          </p>
                        </div>
                      </label>
                    ))
                  )}
                </div>
                {selected.length > 0 && (
                  <div className="mt-3 p-3 bg-muted/30 rounded-lg flex justify-between">
                    <span className="text-sm">Total: {selected.length} settlements</span>
                    <span className="font-mono font-bold">{formatCurrency(total)}</span>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowCreate(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreate}>Create Draft Run</Button>
              </div>
            </CardContent>
          </Card>
        )}

        <DataTable
          columns={columns}
          data={runs}
          emptyMessage={dataQ.isLoading ? 'Loading…' : 'No pay runs yet'}
        />
      </div>
);
}
